import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useRootStore } from 'store/rootStore';
import { runInAction, toJS } from 'mobx';
import { useHistory } from 'react-router-dom';
import {
  BrowsingCard,
  FilterSidebar,
  EditableInfoBlock,
  ComparisonBlock,
  ProposalSidebar,
  Button,
  FilterArrowButton,
  useQueryParams,
} from '@insurancemenu/imenu-components-lib';
import { Filter } from '@insurancemenu/imenu-components-lib/dist/types/filterSidebarTypings';
import {
  BrowsingPageType,
  CartBrowsingCriteria,
  Portfolio,
} from '@insurancemenu/imenu-components-lib/dist/types/productCardsTypings';
import { ProductLineId } from '@insurancemenu/imenu-components-lib/dist/types/ProductLineId';
import {
  getProductLinesFromArray,
  PRODUCT_LINES_CONTENT_MAP,
} from '@insurancemenu/imenu-components-lib/dist/utils/ProductLinesHelper';
import GlobalSidebarsLayout from 'components/common/general/layouts/GlobalSidebarsLayout';
import FormsSpinner from 'components/common/loaders/FormSpinner';
import { ArrowBack, Cart, Legal } from '@insurancemenu/imenu-components-lib/dist/icons';
import clsx from 'clsx';
import SaveProposalModal from 'components/browsing/SaveProposalModal';
import RequiredCrossSellModal from 'components/browsing/RequiredCrossSellModal';
import DynamicFormModalDEPRECATED from 'components/common/modals/DynamicFormModal_DEPRECATED';
import { breakpointsValues, colors } from '@insurancemenu/imenu-components-lib/dist/utils';
import ContinuousRatesPullingModal from 'components/browsing/BrowsingContent/ContinuousRatesPullingModal';
import { BrowsingType, Feature } from 'types/domainServiceV2';
import RFQBrowsingCard from '@insurancemenu/imenu-components-lib/dist/components/productCard/RFQBrowsingCard';
import {
  getBrowsingUserActionByRateSegment,
  getRFQProposalChecklistURL,
} from 'global/checklists-helpers/RFQProposalChecklistHelper';
import UWGuidelines from 'components/common/general/UWGuidelines';
import { redirectToRoute } from 'routes/redirectToRoute';
import { RouteV2Id } from 'routes/routesConfigV2';
import { getRequiredCrossSellProductLineId } from 'global/utils/crossSellHelper';
import { BrowsingCardWrapperStyled, createStyles, CrossSellPlanLabelStyled } from './styles';
import ResetBlock from './ResetBlock';
import MarkAsStockModal from '../MarkAsStockModal';
import AddTemplateVariantModal from '../AddTemplateVariantModal';

export interface BrowsingPageTypeInfo {
  backButtonTitle: string;
  filterSidebarTitle: string;
  backButtonFunc: () => void;
}
export type BrowsingTypesMap = {
  [key in BrowsingPageType]: BrowsingPageTypeInfo;
};

const BrowsingContent = observer((): JSX.Element => {
  // Mobx store
  const {
    browsingStore: {
      attrsForSelectedProductLine,
      catalog,
      productLines,
      group,
      cart,
      allPortfolios,
      comparedPortfolios,
      filteredPortfolios,
      rateValues,
      currentProductLineId,
      loading,
      continuousPullingRatesModalOpened,
      isProposalSidebarOpened,
      calculateRatesAttempts,
      unusedProductLineIds,
      uwGuidelines,
      // get all data
      getPortfolios,
      getCatalog,
      collectInitialData,
      getDependentProductLinesForProposal,
      // browsing card methods
      onAddToProposal,
      onRemoveFromProposal,
      onViewPlanDetails,
      onCompare,
      compareVariants,
      compareAllPortfoliosInPl,
      onConfigurePortfolio,
      onRemoveFromComparison,
      clearComparedPortfolios,
      compareSelectedPortfolios,
      compareAllPortfolios,
      updateCart,
      setCurrentProductLineId,
      setFilteredPortfolios,
      setLoading,
      setProposalSidebarOpeningStatus,
      getAllCarriersFromProducts,
      setContinuousPullingRatesModal,
      getProductLineBrowsingType,
      onContinueChecklist,
      confirmSaveCustomProposal,
      onAddToRFQProposal,
      getGuidelines,
      resetBrowsingPageState,
      requiredCrossSellModal,
      areRequiredCrossSellPlansAdded,
      openRequiredCrossSellModal,
    },
    addVariantModalStore: { openAddVariantModal, closeAddVariantModal },
    markAsStockModalStore: { openMarkAsStockModal, closeMarkAsStockModal },
    browsingFilterStore: { filterPortfoliosBy },
    browsingCriteriaModalStore: {
      openBrowsingCriteriaModal,
      closeBrowsingCriteriaModal,
      saveBrowsingCriteria,
      showBrowsingCriteriaModal,
      dynamicForm,
      form,
      loading: changeBcModalLoading,
    },
    saveProposalModalStore: { saveForm, openSaveProposalModal, closeSaveProposalModal, confirmSaveProposalModal },
    userStore: { sponsorId, imAgencyId, userRoles },
    domainStoreV2: { domainHeader, showQuotingUWGuidelinesFeature },
  } = useRootStore();

  const { cartName, cartItems, lastUpdateBrowsingCriteria, id } = cart;

  const convertedAllPortfolios = allPortfolios.map((portfolio) => toJS(portfolio)).slice();
  const convertedFilteredPortfolios = filteredPortfolios.map((portfolio) => toJS(portfolio)).slice();
  const convertedMarketingTags = catalog.marketingTags.map((tag) => toJS(tag)).slice();
  const convertedProductLineAttrs = attrsForSelectedProductLine.map((attr) => toJS(attr)).slice();
  const convertedProductLines = productLines.map((line) => toJS(line)).slice();
  const convertedCartItems = cartItems.map((item) => toJS(item)).slice();
  const convertedComparedPortfolios = comparedPortfolios.map((portfolio) => toJS(portfolio)).slice();
  const convertedGroup = toJS(group);
  const convertedRateMaxValues = toJS(rateValues);
  const convertedBC = toJS(lastUpdateBrowsingCriteria);

  const convertedComparedPortfoliosIds = convertedComparedPortfolios.map((portfolio) => portfolio.id);
  const productLineIds = convertedProductLines.map((line) => line.productLineId);

  const [isFilterReset, setIsFilterReset] = useState(false);
  const query = useQueryParams();
  const history = useHistory();

  const groupId = query.get('groupId') || '';
  const cartId = query.get('cartId') || null;
  const periodId = query.get('periodId') || '';

  const browsingPageType = query.get('type') || null;
  const pageType = browsingPageType ? (browsingPageType as BrowsingPageType) : ('browsing' as BrowsingPageType);

  const urlProductLineId = query.get('productLineId') ? (query.get('productLineId') as ProductLineId) : null;
  const firstAvailableProductLineId = convertedProductLines[0]?.productLineId;
  // What line did this visit start with? (URL legacy, else first available — used for init, cancel, renewal)
  const entryProductLineId = urlProductLineId ?? firstAvailableProductLineId;
  // What line is the user on right now? (store wins over entry — used for title, compare label, edit BC, etc.)
  const activeProductLineId = currentProductLineId || urlProductLineId || firstAvailableProductLineId;
  const rateSegment = activeProductLineId ? PRODUCT_LINES_CONTENT_MAP[activeProductLineId].rateSegment : 'GROUP';

  const effectiveDate = convertedBC?.effectiveDate;
  const zipCode = convertedBC?.zipCode;
  const state = convertedBC?.state;
  const county = convertedBC?.county;

  // Cross-sell recommendations: optional product line hints in Save Proposal modal (feature-flag driven, non-blocking)
  const showCrossSellPLines =
    domainHeader.properties?.availableFeatures?.some((feature) => feature === Feature.CROSS_SELL_MODAL) || false;

  const dependentProductLinesForProposal = getDependentProductLinesForProposal();
  const areProductLinesValid = dependentProductLinesForProposal.length === 0;

  const classes = createStyles({ comparedPortfoliosQuantity: convertedComparedPortfolios.length });

  const resetFilter = (): void => {
    // reset all filter changes
    setIsFilterReset(true);
    setFilteredPortfolios(allPortfolios); // reset filtered portfolios
  };

  const resetData = (): void => {
    resetBrowsingPageState();
    resetFilter();
    // After the full page redesign, a new store approach will be implemented.
    // The store will be automatically created each time the page is loaded,
    // eliminating the need for manual steps like closing all modals and clearing data.
    closeBrowsingCriteriaModal();
    closeSaveProposalModal();
    closeAddVariantModal();
    closeMarkAsStockModal();
    requiredCrossSellModal.closeModal();
  };

  const updateProductLineData = async (bc: CartBrowsingCriteria, pLineId: ProductLineId): Promise<void> => {
    if (!currentProductLineId) throw new Error('No currentProductLineId in the store');
    setLoading(true);

    if (pLineId !== urlProductLineId) {
      // clear comparison block if we switch between product lines
      clearComparedPortfolios();

      history.push(
        periodId
          ? `/browsing?periodId=${periodId}&groupId=${groupId}&productLineId=${pLineId}&cartId=${cartId}&type=${pageType}`
          : `/browsing?groupId=${groupId}&productLineId=${pLineId}&cartId=${cartId}&type=${pageType}`,
      );
    }
    const requests = [
      getCatalog(effectiveDate, rateSegment, pLineId, state),
      getPortfolios({
        bc,
        rateSegment,
        sponsorId,
        agencyId: imAgencyId,
        productLineId: pLineId,
        groupId,
      }),
      getGuidelines(pLineId, effectiveDate, bc),
    ];
    await Promise.all(requests).then(() => {
      setIsFilterReset(true); // reset all filters if they were set for previous product line
      setLoading(false);
    });
  };

  // Action when u clicks on the Product lines in the Sidebar
  // On click we change Product Line Id in the URL, so we will know what Browsing Criteria modal to open
  // But in the store we will save new Product Line Id, only after submitting the modal
  const changeProductLine = (selectedProductLineId: ProductLineId): void => {
    setCurrentProductLineId(selectedProductLineId); // we firstly change product line id in the URL and then update the content of the page
    const browsingType = getProductLineBrowsingType(selectedProductLineId);

    switch (browsingType) {
      case 'RFQ':
        void updateProductLineData(convertedBC, selectedProductLineId);
        break;

      case 'SHELF':
      default:
        void openBrowsingCriteriaModal(
          cart,
          selectedProductLineId,
          convertedProductLines.find((line) => line.productLineId === selectedProductLineId)?.bcFormId || '',
        );
        break;
    }
  };

  useEffect(() => {
    if (cartId) {
      void collectInitialData(groupId, cartId, periodId, pageType, urlProductLineId);
    }
    return (): void => resetData();
  }, []);

  useEffect(() => {
    if (id === '' || !entryProductLineId) {
      return;
    }
    setCurrentProductLineId(entryProductLineId);

    if (convertedProductLines.length > 0 && !convertedBC[entryProductLineId]) {
      changeProductLine(entryProductLineId);
    }
  }, [productLines, id]);

  const BrowsingPageTypesInfo: BrowsingTypesMap = Object.freeze({
    browsing: {
      backButtonTitle: 'Back to group profile',
      filterSidebarTitle: convertedGroup.name,
      backButtonFunc: () => {
        resetData();
        history.push(`/group/${groupId}`);
      },
    },

    template: {
      backButtonTitle: 'Back to favorite proposals',
      filterSidebarTitle: cartName,
      backButtonFunc: () => {
        resetData();
        history.push(`/favorite-proposals`);
      },
    },
    renewal: {
      backButtonTitle: 'Back to group profile',
      filterSidebarTitle: convertedGroup.name,
      backButtonFunc: () => {
        resetData();
        history.push(`/group/${groupId}`);
      },
    },
    rfq: {
      backButtonTitle: 'Back to checklist',
      filterSidebarTitle: convertedGroup.name,
      backButtonFunc: () => {
        resetData();
        history.push(getRFQProposalChecklistURL({ periodId, cartId: id }));
      },
    },
    initiate: {
      backButtonTitle: 'Back to Add Quote',
      filterSidebarTitle: convertedGroup.name,
      backButtonFunc: () => {
        resetData();
        redirectToRoute(RouteV2Id.INITIATE_SHELF_ADD_QUOTE, {
          groupId,
          periodId,
          cartId: cartId || '',
        });
      },
    },
  });

  // TODO this function is called some times after reset filter because each component in filter resets value. This happens only with range components
  // Maybe there is a way to optimize this.
  const filterPortfolios = (filters: Filter): void => {
    // update reset status. It allows filter component save and send values
    setIsFilterReset(false);
    // On every new filter value, we should search available portfolios from full list
    const updatedPortfolios = filterPortfoliosBy([...convertedAllPortfolios], filters, convertedProductLineAttrs);

    setFilteredPortfolios(updatedPortfolios);
  };

  const removePortfolioFromProposal = (portfolioId: string): void => {
    void onRemoveFromProposal(portfolioId, true, pageType);
  };

  const addToRFQProposal = (portfolioId: string, portfolioName: string): void => {
    void onAddToRFQProposal(portfolioId, portfolioName, periodId);
  };

  const saveProposal = async (): Promise<void> => {
    if (!areRequiredCrossSellPlansAdded) {
      openRequiredCrossSellModal();
      return;
    }

    switch (pageType) {
      case 'rfq':
        await confirmSaveCustomProposal(
          toJS(cart),
          periodId,
          getBrowsingUserActionByRateSegment(rateSegment).userActions,
        );
        if (rateSegment === 'GROUP') {
          redirectToRoute(RouteV2Id.GROUP_HUB, { groupId, periodId, cartId: cartId || '' });

          resetData();
        } else {
          BrowsingPageTypesInfo.rfq.backButtonFunc();
          resetData();
        }
        break;
      case 'initiate':
        BrowsingPageTypesInfo.initiate.backButtonFunc();
        break;
      default:
        openSaveProposalModal(group.name);
    }
  };

  // Action in the header of the page
  const openEditBC = (): void => {
    if (!activeProductLineId) {
      return;
    }
    changeProductLine(activeProductLineId);
  };

  const saveModalChanges = async (): Promise<void> => {
    if (!currentProductLineId) throw new Error('Modal cannot be opened without currentProductLineId in the store');
    closeBrowsingCriteriaModal();
    setLoading(true);
    clearComparedPortfolios();

    const updatedCart = await saveBrowsingCriteria();
    await runInAction(async () => {
      updateCart(updatedCart);
      await updateProductLineData(updatedCart.lastUpdateBrowsingCriteria, currentProductLineId);
      setLoading(false);
    });
  };

  const cancelModalChanges = (): void => {
    if (entryProductLineId && currentProductLineId !== entryProductLineId) {
      setCurrentProductLineId(entryProductLineId);
    }
    closeBrowsingCriteriaModal();
  };

  const ProposalSidebarButton = (openingStatus: boolean): JSX.Element => {
    return (
      <Button
        color="primary"
        onClick={(): void => {
          setProposalSidebarOpeningStatus(openingStatus);
        }}
        size="medium"
        leftIconProp={<Cart />}
      >
        {`${convertedCartItems.length} in Proposal`}
      </Button>
    );
  };

  const changeBcModalCancel = (): JSX.Element => {
    return convertedFilteredPortfolios.length === 0 ? (
      <Button
        type="outlined"
        onClick={(): void => {
          history.goBack();
        }}
        size="large"
        className={classes.cancelButton}
      >
        Go Back
      </Button>
    ) : (
      <Button type="outlined" onClick={cancelModalChanges} size="large" className={classes.cancelButton}>
        Cancel
      </Button>
    );
  };

  const showPortfoliosContent = (list: Portfolio[], browsingType: BrowsingType): JSX.Element[] | JSX.Element => {
    if (list.length === 0 && !loading) {
      return <ResetBlock resetFilter={resetFilter} />;
    }
    const portfoliosList = list.map((portfolio): JSX.Element => {
      const currentCartItem = convertedCartItems.find(({ portfolioId }) => portfolioId === portfolio.id);

      const crossSellProductLineId = getRequiredCrossSellProductLineId(portfolio.id);
      const crossSellProductLineLabel = crossSellProductLineId
        ? PRODUCT_LINES_CONTENT_MAP[crossSellProductLineId].label
        : undefined;

      switch (browsingType) {
        case 'RFQ':
          return (
            <RFQBrowsingCard
              key={portfolio.id}
              portfolio={portfolio}
              productLineAttributes={convertedProductLineAttrs}
              marketingTags={convertedMarketingTags}
              cartItem={currentCartItem}
              selectedForCompare={convertedComparedPortfoliosIds.includes(portfolio.id)}
              className={classes.browsingCard}
              breakpoint={breakpointsValues.md}
              userRoles={userRoles.map((role) => toJS(role)).slice()}
              onAddToProposal={addToRFQProposal}
              onRemoveFromProposal={removePortfolioFromProposal}
              onCompare={onCompare}
              onViewPlanDetails={(): void => {
                onViewPlanDetails(portfolio.id, cartId || '', history.push);
              }}
              onAddAnotherVariant={(templateId: string): void => {
                openAddVariantModal({ id: templateId, name: portfolio.name, internalName: '' });
              }}
              onMarkAsStock={(templateId: string): void => {
                openMarkAsStockModal(portfolio.name, templateId);
              }}
              onContinueChecklist={(templateId: string): void => {
                onContinueChecklist({ templateId, periodId, cartId: cartId || '', groupId }, history.push);
              }}
            />
          );

        case 'SHELF':
        default:
          return (
            <BrowsingCardWrapperStyled
              $hasCrossSellPlan={!!crossSellProductLineId}
              $isAddedToCart={!!currentCartItem}
              $hasSeveralProducts={portfolio.products.length > 1}
              key={portfolio.id}
            >
              {crossSellProductLineLabel && (
                <CrossSellPlanLabelStyled>
                  <span>Requires adding a {crossSellProductLineLabel} plan</span>
                </CrossSellPlanLabelStyled>
              )}
              <BrowsingCard
                portfolio={portfolio}
                cartId={cartId || ''}
                productLineAttributes={convertedProductLineAttrs}
                marketingTags={convertedMarketingTags}
                cartItem={currentCartItem}
                onAddToProposal={onAddToProposal}
                onRemoveFromProposal={removePortfolioFromProposal}
                onCompare={onCompare}
                onCompareVariants={(portfolioId: string, currentCartId: string): void => {
                  compareVariants(portfolioId, currentCartId, history.push);
                }}
                onViewPlanDetails={(portfolioId: string, currentCartId: string): void => {
                  onViewPlanDetails(portfolioId, currentCartId, history.push);
                }}
                onConfigurePortfolio={
                  portfolio.configurable
                    ? (portfolioId: string): void => {
                        onConfigurePortfolio(portfolioId, cartId || '', history.push);
                      }
                    : undefined
                }
                selectedForCompare={convertedComparedPortfoliosIds.includes(portfolio.id)}
                className={clsx(classes.browsingCard, crossSellProductLineId ? classes.withCrossSellPlan : '')}
                breakpoint={breakpointsValues.md}
              />
            </BrowsingCardWrapperStyled>
          );
      }
    });

    return <div className={classes.cardsList}>{portfoliosList}</div>;
  };

  const onSaveProposal = async (): Promise<void> => {
    if (saveForm.isFormValid()) {
      const address = { zip: zipCode, state, county };
      await confirmSaveProposalModal(cart, group, saveForm.fields.name.value, address, catalog).then(() => {
        history.push(`/group/${group.id}`);
        resetData();
      });
    }
  };

  return (
    <div className={classes.browsingContentWrapper}>
      {loading && <FormsSpinner display={loading} spinnerStyle={classes.browsingSpinner} />}
      <GlobalSidebarsLayout
        contentStyle={classes.sidebarContentLayout}
        leftSection={
          <FilterSidebar
            marketingTags={convertedMarketingTags}
            carriers={getAllCarriersFromProducts(convertedAllPortfolios)}
            productLineAttributes={convertedProductLineAttrs}
            rates={convertedRateMaxValues}
            filterPortfolios={filterPortfolios}
            productLineIds={productLineIds}
            groupInfo={{
              groupName: BrowsingPageTypesInfo[pageType].filterSidebarTitle,
              effectiveDate,
              address: `${state} ${zipCode}`,
            }}
            onProductLineChange={(selectedProductLineId: ProductLineId): void => {
              if (currentProductLineId !== selectedProductLineId) {
                changeProductLine(selectedProductLineId);
              }
            }}
            shouldBeReset={isFilterReset}
            activeProductLineId={currentProductLineId}
            type={pageType}
          />
        }
        centerSection={
          <div>
            <div className={classes.browsingHeader}>
              <div className={classes.headerTop}>
                <Button
                  leftIconProp={<ArrowBack />}
                  type="text"
                  size="medium"
                  onClick={BrowsingPageTypesInfo[pageType].backButtonFunc}
                  className={classes.backButton}
                >
                  {BrowsingPageTypesInfo[pageType].backButtonTitle}
                </Button>
                <FilterArrowButton
                  direction="left"
                  buttonWrapperStyle={clsx(classes.openSidebarButton, isProposalSidebarOpened && classes.hiddenButton)}
                  button={ProposalSidebarButton(true)}
                  onActionClick={(): void => {
                    setProposalSidebarOpeningStatus(true);
                  }}
                />

                {activeProductLineId && (
                  <h2 className={classes.title}>
                    {`Browsing ${PRODUCT_LINES_CONTENT_MAP[activeProductLineId].label}`}
                  </h2>
                )}
              </div>

              <>
                {/* This check is needed because of Map type of browsing criteria */}
                {!!cart.lastUpdateBrowsingCriteria.shortName &&
                  !!currentProductLineId &&
                  // Only SHELF type has a short name to display in this block
                  getProductLineBrowsingType(currentProductLineId) === 'SHELF' && (
                    <EditableInfoBlock
                      title={cart.lastUpdateBrowsingCriteria.shortName[currentProductLineId]}
                      backgroundColor={colors.neutral.white}
                      maxWidth="70%"
                      actionName="Edit Browsing Criteria"
                      onEditInfo={openEditBC}
                      className={classes.bcBlock}
                    />
                  )}

                <Button
                  type="text"
                  color="primary"
                  leftIconProp={<Legal />}
                  size="small"
                  className={classes.compareButton}
                  onClick={(): void => {
                    compareAllPortfolios(history.push);
                  }}
                >
                  {activeProductLineId
                    ? `Compare all ${PRODUCT_LINES_CONTENT_MAP[activeProductLineId].label} Plans`
                    : 'Compare all Plans'}
                </Button>
                {showQuotingUWGuidelinesFeature && currentProductLineId && uwGuidelines.length > 0 ? (
                  <UWGuidelines uwGuidelines={uwGuidelines} />
                ) : null}
              </>
            </div>

            {/* Sticky block */}
            <ComparisonBlock
              comparedPortfolios={convertedComparedPortfolios}
              compareBlockStyles={classes.compareBlock}
              onRemoveFromComparison={onRemoveFromComparison}
              hideCompareBlock={clearComparedPortfolios}
              compareSelectedPortfolios={(): void => {
                compareSelectedPortfolios(history.push);
              }}
            />
            <div className={classes.browsingContent}>
              <p className={classes.portfoliosQuantity}>
                Available Products: <b>{convertedFilteredPortfolios.length}</b>
              </p>
              {showPortfoliosContent(convertedFilteredPortfolios, getProductLineBrowsingType(activeProductLineId))}
            </div>
          </div>
        }
        rightSection={
          <ProposalSidebar
            sidebarStyles={classes.proposalSidebarContent}
            productLineIds={
              pageType === ('renewal' as BrowsingPageType) && entryProductLineId ? [entryProductLineId] : productLineIds
            }
            cartItems={convertedCartItems || []}
            onProductLineChange={(selectedProductLineId: ProductLineId): void => {
              if (currentProductLineId !== selectedProductLineId) {
                changeProductLine(selectedProductLineId);
              }
            }}
            submitButton={
              // Template type has autosave
              pageType === 'template' ? undefined : (
                <div className={classes.saveProposalButtonWrapper}>
                  <Button
                    size="large"
                    color="custom"
                    disabled={convertedCartItems.length === 0 || !areProductLinesValid || loading}
                    customColor={colors.green[600]}
                    onClick={saveProposal}
                  >
                    {pageType === 'initiate' ? 'Save' : 'Save Proposal'}
                  </Button>
                  {!areProductLinesValid && (
                    <span>Elect {`${getProductLinesFromArray(dependentProductLinesForProposal)} to proceed`}</span>
                  )}
                </div>
              )
            }
            onRemoveFromProposal={(portfolioId: string): void => {
              void onRemoveFromProposal(portfolioId, false, pageType);
            }}
            isOpen={isProposalSidebarOpened}
            closeButton={ProposalSidebarButton(false)}
            onActionClick={(): void => {
              setProposalSidebarOpeningStatus(false);
            }}
            onCompareAllPortfoliosInPl={(productLineId: ProductLineId): void => {
              compareAllPortfoliosInPl(productLineId, history.push);
            }}
            type={pageType || undefined}
          />
        }
      />
      <DynamicFormModalDEPRECATED
        show={showBrowsingCriteriaModal}
        closable={convertedFilteredPortfolios.length !== 0}
        onClose={cancelModalChanges}
        onAction={saveModalChanges}
        dynamicForm={dynamicForm}
        form={form}
        loading={changeBcModalLoading}
        footerCancelButton={changeBcModalCancel()}
        formStyles={classes.bcForm}
      />
      {rateSegment !== 'CUSTOM' && (
        <SaveProposalModal
          unusedProductLineIds={unusedProductLineIds}
          showCrossSellPLines={showCrossSellPLines}
          changeProductLine={changeProductLine}
          onSubmit={onSaveProposal}
        />
      )}
      <RequiredCrossSellModal modalStore={requiredCrossSellModal} changeProductLine={changeProductLine} />
      <ContinuousRatesPullingModal
        show={continuousPullingRatesModalOpened}
        onClose={(): void => setContinuousPullingRatesModal(false)}
        attemptNumber={calculateRatesAttempts}
      />
      {/* RFQ Modals */}
      <AddTemplateVariantModal />
      <MarkAsStockModal />
    </div>
  );
});

export default BrowsingContent;
